import clsx from "clsx";
import React, { useEffect } from "react";
import BranchLink from "web/react/components/branch-link/branch-link";
import GenericStickyModal from "web/react/components/generic-sticky-modal/generic-sticky-modal";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import analytics from "web/script/analytics/analytics";
import { gettext, gettextNoop } from "web/script/modules/django-i18n";
import environment from "web/script/modules/environment";
import styles from "./send-to-app-checklist-modal.module.css";

const TITLE_KEYS = {
    // "Discover all fashion you love in one place"
    title_1: gettextNoop("app_overlay_block.feed.title_1"),
    // "All the items you want at the best price"
    title_2: gettextNoop("app_overlay_block.feed.title_2"),
    // "Zero stress shopping with sale notifications on your favourite items"
    title_3: gettextNoop("app_overlay_block.feed.title_3"),
    // "Daily personalised and curated edits"
    title_4: gettextNoop("app_overlay_block.feed.title_4"),
};

const benefitList = [
    {
        // Biggest fashion inventory
        label: gettext("app_overlay_block.biggest_inventory.label"),
        check1: true,
        check2: true,
    },
    {
        // "Instant sale notifications"
        label: gettext("app_overlay_block.sale_notifications.label"),
        check1: false,
        check2: true,
    },
    {
        // Daily edits for your style
        label: gettext("app_overlay_block.daily_edits.label"),
        check1: false,
        check2: true,
    },
    {
        // Fastest access to best price
        label: gettext("app_overlay_block.fastest_price.label"),
        check1: false,
        check2: true,
    },
];

interface SendToAppChecklistModalProps {
    isOpen: boolean;
    onClose: () => void;
    url: string;
    analyticsEventLabel: string;
    downloadCTA?: string;
    onCtaClick?: React.EventHandler<React.MouseEvent>;
    onAfterOpen?: () => void;
}

function SendToAppChecklistModal({
    isOpen,
    onClose,
    url,
    analyticsEventLabel,
    downloadCTA,
    onCtaClick,
    onAfterOpen,
}: SendToAppChecklistModalProps): JSX.Element {
    const titleVariant = String(environment.getFeature("mg_sticky_bottom_web_to_app_modal_title"));
    const title = gettext(TITLE_KEYS[titleVariant]);

    // "Continue on the Lyst app for free"
    downloadCTA = downloadCTA ? downloadCTA : gettext("app_overlay_block.continue.cta");
    const webLabel = gettext("app_overlay_block.lyst_web.label");
    const appLabel = gettext("app_overlay_block.lyst_app.label");

    function onModalClose(): void {
        analytics.event("app_overlay", "modal_dismissed", analyticsEventLabel);
        onClose();
    }

    useEffect(() => {
        if (isOpen) {
            analytics.event("app_overlay", "modal_open", analyticsEventLabel);
        }
    }, [isOpen, analyticsEventLabel]);

    function makeBenefit(benefitData, index): JSX.Element {
        return (
            <div
                key={`benefitRow-${index}`}
                className={clsx(styles.benefitRow, {
                    [styles.firstRow]: index === 0,
                    [styles.evenRow]: index % 2 === 0,
                })}
            >
                <span className={styles.benefitLabel}>{benefitData.label}</span>
                {benefitData.check1 && <SVGIcon name="tick" className={styles.tickIcon} />}
                {benefitData.check2 && <SVGIcon name="tick" className={styles.tickIcon} />}
            </div>
        );
    }

    return (
        <GenericStickyModal isOpen={isOpen} onClose={onModalClose} onAfterOpen={onAfterOpen}>
            <>
                <h1 className={styles.title}>{title}</h1>
                <div className={styles.subTitleHolder}>
                    <span className={styles.webSubTitle}>{webLabel}</span>
                    <span className={styles.appSubTitle}>{appLabel}</span>
                </div>
                <div className={styles.benefitsTable}>
                    {benefitList.map((benefit, index) => {
                        return makeBenefit(benefit, index);
                    })}
                </div>
                <BranchLink
                    title={downloadCTA}
                    className={styles.button}
                    branchLink={url}
                    eventLabel={analyticsEventLabel}
                    showAppIcon={true}
                    primaryButton
                    beforeOnClick={onCtaClick}
                />
            </>
        </GenericStickyModal>
    );
}

export default SendToAppChecklistModal;
